import { ArrowRight, Package, ShieldCheck, Waves } from 'lucide-react';
import { formatUnits, parseUnits } from 'viem';
import { useAccount, useReadContract } from 'wagmi';
import { ROUTER_ABI, SALT_RWA_ABI } from '../web3/abis';
import { TARGET_CHAIN } from '../web3/config';
import { getDeployment } from '../web3/contracts';
import type { NavView } from './Header';
import SaltPriceChart from './rwa/SaltPriceChart';
import WarehouseComposition from './rwa/WarehouseComposition';

function fmtKg(value: bigint | undefined) {
  if (value === undefined) return '—';
  return `${Number(formatUnits(value, 18)).toLocaleString('en-US', { maximumFractionDigits: 0 })} kg`;
}

// RWA is the flagship of the exchange, so the Overview leads with a condensed
// Salt-RWA panel: the live SALT/USDT chart and the warehouse-inventory donut,
// with a jump into the full RWA desk. Renders nothing on chains without RWA.
export default function RwaSpotlight({ onNavigate }: { onNavigate: (view: NavView) => void }) {
  const { chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const rwa = deployment?.saltRwa;

  const { data: totalSupply } = useReadContract({
    address: rwa?.token,
    abi: SALT_RWA_ABI,
    functionName: 'totalSupply',
    chainId: readChainId,
    query: { enabled: Boolean(rwa), refetchInterval: 10000 },
  });

  const { data: totalRedeemed } = useReadContract({
    address: rwa?.token,
    abi: SALT_RWA_ABI,
    functionName: 'totalRedeemed',
    chainId: readChainId,
    query: { enabled: Boolean(rwa), refetchInterval: 10000 },
  });

  const { data: referencePrice } = useReadContract({
    address: rwa?.token,
    abi: SALT_RWA_ABI,
    functionName: 'referencePriceUsd',
    chainId: readChainId,
    query: { enabled: Boolean(rwa) },
  });

  const { data: poolQuote } = useReadContract({
    address: deployment?.router,
    abi: ROUTER_ABI,
    functionName: 'getAmountsOut',
    args: rwa && deployment ? [parseUnits('1', 18), [rwa.token, deployment.tokens.USDT]] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(rwa && deployment), refetchInterval: 10000 },
  });
  const poolPrice = poolQuote ? Number(formatUnits(poolQuote[1], 18)) : undefined;

  if (!rwa) return null;

  return (
    <section className="rounded-xl border border-tide-500/30 bg-gradient-to-br from-tide-500/[0.05] to-transparent p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-abyss-850 border border-abyss-700 flex items-center justify-center text-tide-400">
            <Waves size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foam-100 flex items-center gap-2">
              Salt RWA
              <span className="text-[10px] font-semibold uppercase tracking-wide text-tide-400 bg-tide-400/10 px-1.5 py-0.5 rounded">
                핵심 자산
              </span>
            </h3>
            <p className="text-[11px] text-foam-500 mt-0.5">실물 소금 창고 보관증 토큰 — 1 SALT = 소금 1 kg</p>
          </div>
        </div>
        <button
          onClick={() => onNavigate('RWA')}
          className="flex items-center gap-1 text-xs font-semibold text-tide-400 hover:text-tide-300"
        >
          RWA 데스크 전체 보기
          <ArrowRight size={14} />
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MiniStat icon={Package} label="토큰화 재고" value={fmtKg(totalSupply)} />
        <MiniStat icon={ShieldCheck} label="상환 완료" value={fmtKg(totalRedeemed)} />
        <MiniStat
          icon={Waves}
          label="발행사 기준가"
          value={referencePrice !== undefined ? `$${(Number(referencePrice) / 1e8).toFixed(2)}/kg` : '—'}
        />
        <MiniStat
          icon={Waves}
          label="실시간 풀 가격"
          value={poolPrice !== undefined ? `${poolPrice.toFixed(4)} USDT` : '—'}
          accent
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 min-w-0">
          <SaltPriceChart
            poolPrice={poolPrice}
            referenceUsd={referencePrice !== undefined ? Number(referencePrice) / 1e8 : undefined}
          />
        </div>
        <div className="min-w-0">
          <WarehouseComposition
            tokenAddress={rwa.token}
            chainId={readChainId}
            totalSupply={totalSupply}
            totalRedeemed={totalRedeemed}
          />
        </div>
      </div>
    </section>
  );
}

function MiniStat({
  icon: Icon,
  label,
  value,
  accent = false,
}: {
  icon: typeof Package;
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-850 min-w-0">
      <div className="flex items-center gap-1.5 text-[11px] text-foam-500 min-w-0">
        <Icon size={12} className={`shrink-0 ${accent ? 'text-tide-400' : ''}`} />
        <span className="truncate">{label}</span>
      </div>
      <div
        className={`text-base font-semibold mt-1 tabular-nums truncate ${accent ? 'text-tide-400' : 'text-foam-100'}`}
      >
        {value}
      </div>
    </div>
  );
}
